import { useEffect, useRef, useState, useCallback } from "react";

const STORAGE_KEY = "greg_notified_reminders_v1";
const POLL_INTERVAL_MS = 60 * 1000; // 1 minuto
const LEAD_TIME_MIN = 30; // notifica até 30 min antes
const ENABLED_KEY = "greg_notifications_enabled";
const DAILY_SUMMARY_KEY = "greg_daily_summary_last_date";
const DAILY_SUMMARY_MIN_HOUR = 8; // só dispara a partir das 08h locais

function readNotifiedMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const cleaned = {};
    for (const [id, ts] of Object.entries(parsed)) {
      if (ts > cutoff) cleaned[id] = ts;
    }
    return cleaned;
  } catch {
    return {};
  }
}

function writeNotifiedMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

function getInitialPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/**
 * Hook que dispara notificações nativas do navegador para lembretes próximos.
 * Faz polling em /api/reminders/upcoming a cada 60s enquanto a aba estiver carregada.
 */
export function useReminderNotifications(apiCall) {
  const [permission, setPermission] = useState(getInitialPermission);
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(ENABLED_KEY) === "1";
  });
  const intervalRef = useRef(null);

  const supported = permission !== "unsupported";

  const requestPermission = useCallback(async () => {
    if (!supported) return "unsupported";
    let result = Notification.permission;
    if (result === "default") {
      result = await Notification.requestPermission();
    }
    setPermission(result);
    if (result === "granted") {
      setEnabled(true);
      localStorage.setItem(ENABLED_KEY, "1");
    }
    return result;
  }, [supported]);

  const disable = useCallback(() => {
    setEnabled(false);
    localStorage.setItem(ENABLED_KEY, "0");
  }, []);

  const fireNotification = useCallback((title, body, tag) => {
    try {
      const n = new Notification(title, {
        body,
        tag,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        silent: false,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch (e) {
      console.warn("Notification fail", e);
    }
  }, []);

  const maybeFireDailySummary = useCallback(async () => {
    if (!enabled || permission !== "granted" || !apiCall) return;
    const now = new Date();
    if (now.getHours() < DAILY_SUMMARY_MIN_HOUR) return;
    const today = now.toISOString().slice(0, 10);
    const last = localStorage.getItem(DAILY_SUMMARY_KEY);
    if (last === today) return;
    try {
      const [expRes, remRes] = await Promise.all([
        apiCall("get", "/dashboard/today-expectation"),
        apiCall("get", "/reminders/upcoming"),
      ]);
      const exp = expRes.data || {};
      const reminders = remRes.data || [];
      const todayStr = now.toISOString().slice(0, 10);
      const remindersToday = reminders.filter((r) => {
        if (!r.reminder_date || r.is_done) return false;
        return new Date(r.reminder_date).toISOString().slice(0, 10) === todayStr;
      }).length;
      const totalApps = exp?.counts?.total ?? 0;
      const expectedActive = exp?.counts?.expected ?? 0;
      const expectedRevenue = Number(exp?.expected_total ?? 0);
      const fmt = expectedRevenue.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
      const parts = [
        `${expectedActive} agendamento${expectedActive === 1 ? "" : "s"} ativo${expectedActive === 1 ? "" : "s"}`,
        `${fmt} previstos`,
        `${remindersToday} lembrete${remindersToday === 1 ? "" : "s"}`,
      ];
      const title = "Resumo do dia · Greg One";
      const body = `${parts.join(" • ")}${totalApps > expectedActive ? ` (+${totalApps - expectedActive} cancelados/faltas)` : ""}`;
      fireNotification(title, body, `daily-summary-${today}`);
      localStorage.setItem(DAILY_SUMMARY_KEY, today);
    } catch (e) {
      // silencioso
    }
  }, [apiCall, enabled, permission, fireNotification]);

  const checkTasks = useCallback(async () => {
    if (!enabled || permission !== "granted" || !apiCall) return;
    try {
      const res = await apiCall("get", "/tasks/today/summary?scope=mine");
      const data = res.data || {};
      const candidates = [...(data.overdue || []), ...(data.today || [])];
      const notified = readNotifiedMap();
      const now = Date.now();
      let changed = false;
      for (const t of candidates) {
        const key = `task-${t.task_id}`;
        if (notified[key]) continue;
        const isOverdue = (data.overdue || []).some((x) => x.task_id === t.task_id);
        const prefix = isOverdue ? "Tarefa atrasada" : "Tarefa para hoje";
        const due = t.due_date
          ? new Date(t.due_date).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
          : "Sem prazo";
        fireNotification(`${prefix}: ${t.title}`, `${t.description ? t.description.slice(0, 80) : ""}\n${due}`, key);
        notified[key] = now;
        changed = true;
      }
      // Aprovações pendentes (silencioso se 403 = não é gerente)
      try {
        const aRes = await apiCall("get", "/tasks/today/summary?scope=to_approve");
        for (const t of aRes.data?.to_approve || []) {
          const key = `approve-${t.task_id}`;
          if (notified[key]) continue;
          fireNotification(`Aguardando sua aprovação: ${t.title}`, `Concluída por ${t.completed_by_name || ""}`, key);
          notified[key] = now;
          changed = true;
        }
      } catch {
        /* não é gerente, ok */
      }
      if (changed) writeNotifiedMap(notified);
    } catch {
      /* silent */
    }
  }, [apiCall, enabled, permission, fireNotification]);

  const checkReminders = useCallback(async () => {
    if (!enabled || permission !== "granted" || !apiCall) return;
    try {
      const res = await apiCall("get", "/reminders/upcoming");
      const reminders = res.data || [];
      const notified = readNotifiedMap();
      const now = Date.now();
      const horizon = now + LEAD_TIME_MIN * 60 * 1000;
      let changed = false;
      for (const r of reminders) {
        if (!r.reminder_date || r.is_done) continue;
        const rid = r.note_id || r.id;
        if (!rid) continue;
        const due = new Date(r.reminder_date).getTime();
        const isOverdue = due < now;
        const isImminent = due >= now && due <= horizon;
        if (!isOverdue && !isImminent) continue;
        if (notified[rid]) continue; // já notificado nas últimas 24h
        const dueStr = new Date(r.reminder_date).toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
        const prefix = isOverdue ? "Atrasado" : "Em breve";
        const clientPart = r.client_name ? ` - ${r.client_name}` : "";
        const title = `${prefix}: lembrete${clientPart}`;
        const body = `${r.content || r.title || "Lembrete"}\n${dueStr}`;
        fireNotification(title, body, `reminder-${rid}`);
        notified[rid] = now;
        changed = true;
      }
      if (changed) writeNotifiedMap(notified);
    } catch (e) {
      // silencioso
    }
  }, [apiCall, enabled, permission, fireNotification]);

  useEffect(() => {
    if (!enabled || permission !== "granted") {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    // primeira checagem imediata
    checkReminders();
    checkTasks();
    maybeFireDailySummary();
    intervalRef.current = setInterval(() => {
      checkReminders();
      checkTasks();
      maybeFireDailySummary();
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, permission, checkReminders, checkTasks, maybeFireDailySummary]);

  return {
    supported,
    permission,
    enabled: enabled && permission === "granted",
    requestPermission,
    disable,
  };
}
